import { isSameOrigin, parseOrigin } from '@/util/origin';

// APIM gates the 125 platform product on a subscription key. Declared once here so the
// platform route modules cannot drift, and deliberately NOT applied to
// chatbot.routes.ts, which calls a different host and must not receive this key.
export const PLATFORM_HEADERS: Record<string, string> = {
	'Ocp-Apim-Subscription-Key': import.meta.env.VITE_125_PLATFORM_SUBSCRIPTION_KEY ?? '',
};

/**
 * Origin of the 125 platform API, or null when it is not configured.
 *
 * This is what `api.ts` matches a request against to decide which token authorises
 * it: the platform token for this origin, the depot token for everything else. The
 * platform route modules already build their URLs from the same base URL, so
 * declaring the origin here keeps the routing decision and the URLs from drifting.
 *
 * Null on an absent or unparseable value, and null means no request is a platform
 * request. That fail-closed default matters more than it looks: the base URL resolves
 * to undefined today, because the route modules read VITE_125_PLATFORM_PUBLIC_BASE_URL
 * while .env defines VITE_125_API_PUBLIC_BASE_URL. Until those names are reconciled,
 * every request keeps the depot token it has always had, rather than a loose match on
 * "undefined" handing the platform token out indiscriminately.
 */
export const PLATFORM_ORIGIN: string | null = parseOrigin(
	import.meta.env.VITE_125_PLATFORM_PUBLIC_BASE_URL,
);

/** True when `url` addresses the 125 platform API. False whenever the origin is unset. */
export const isPlatformUrl = (url: string): boolean => {
	return isSameOrigin(
		url,
		PLATFORM_ORIGIN,
		typeof window === 'undefined' ? undefined : window.location.origin,
	);
};
